import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useTheme } from "@mui/material";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "../../Components/ui/carousel.js";
import { getAppConsistentParams } from "../../utils/appUtils.js";
import { getProductUrl } from "../../utils/productUtils.js";
import { formatMoney } from "../../utils/stringUtils.js";
import { useBrandAppConfig } from "../../api/cache/appConfigQueryOptions.js";
import { useProductClickTracking } from "../hooks/useProductClickTracking.js";
import { useConciergeContext } from "../ConciergeContextProvider.js";
import { fetchProductByStacklineSku } from "../api.js";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
const formatReviewCount = (count) => {
  if (count >= 1e3) {
    const k = count / 1e3;
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return count.toLocaleString();
};
const StarIcon = () => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "14",
    height: "14",
    viewBox: "0 0 16 16",
    fill: "none",
    style: { flexShrink: 0 },
    children: /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M15.4819 6.07562C15.4285 5.8977 15.2864 5.76804 15.1153 5.74117L10.3089 4.98558L8.1594 0.273902C8.0829 0.106169 7.92499 0 7.75211 0C7.5792 0 7.42132 0.106169 7.3448 0.273902L5.19518 4.98558L0.388901 5.74117C0.217838 5.76804 0.0756731 5.89771 0.0222526 6.07558C-0.0311937 6.2535 0.0133808 6.44878 0.137199 6.57932L3.61497 10.2469L2.7941 15.4256C2.76485 15.6099 2.83492 15.7962 2.97479 15.9062C3.05391 15.9684 3.14764 16 3.24182 16C3.31413 16 3.38668 15.9814 3.45312 15.9436L7.75208 13.4985L12.0509 15.9435C12.204 16.0306 12.3894 16.0161 12.5292 15.9061C12.6691 15.7962 12.7392 15.6099 12.71 15.4255L11.8888 10.2469L15.367 6.5793C15.4908 6.44879 15.5354 6.2535 15.4819 6.07562H15.4819Z",
        fill: "#ED792F"
      }
    )
  }
);
const CarouselArrowIcon = ({ direction }) => /* @__PURE__ */ jsx(
  "svg",
  {
    width: "8",
    height: "6",
    viewBox: "0 0 11.0575 7.39064",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    style: { width: 8, height: 6, transform: direction === "left" ? "rotate(180deg)" : void 0 },
    children: /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M10.8826 3.27338C10.8825 3.27323 10.8823 3.27301 10.8821 3.27285L7.76778 0.173545C7.53447 -0.0586046 7.1571 -0.0577646 6.92486 0.175575C6.69265 0.408885 6.69354 0.786265 6.92686 1.01846L9.01782 3.0993H0.59602C0.26684 3.0993 0 3.36612 0 3.69531C0 4.0245 0.26684 4.29138 0.59602 4.29138H9.01779L6.9269 6.37216C6.69359 6.60438 6.69269 6.9817 6.9249 7.21506C7.15714 7.44843 7.53454 7.44925 7.76782 7.21707L10.8821 4.11775C10.8823 4.11757 10.8825 4.11738 10.8827 4.1172C11.1161 3.88423 11.1154 3.50563 10.8826 3.27339V3.27338Z",
        fill: "black"
      }
    )
  }
);
const ProductImageCarousel = ({ images, title }) => {
  if (images.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: "tw:w-56 tw:h-56 tw:rounded-lg tw:bg-gray-100 tw:flex-shrink-0" });
  }
  if (images.length === 1) {
    return /* @__PURE__ */ jsx(
      "img",
      {
        src: images[0],
        alt: title,
        className: "tw:w-56 tw:h-56 tw:rounded-lg tw:object-contain tw:flex-shrink-0"
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: "tw:w-56 tw:h-56 tw:flex-shrink-0 tw:relative tw:group", children: /* @__PURE__ */ jsxs(Carousel, { opts: { align: "start", loop: false }, className: "tw:w-full tw:h-full", children: [
    /* @__PURE__ */ jsx(CarouselContent, { className: "tw:h-56 [&>div]:h-full", children: images.map((url, idx) => /* @__PURE__ */ jsx(CarouselItem, { className: "tw:h-full tw:flex tw:items-center tw:justify-center", children: /* @__PURE__ */ jsx(
      "img",
      {
        src: url,
        alt: `${title} - ${idx + 1}`,
        className: "tw:w-56 tw:h-56 tw:object-contain",
        loading: idx === 0 ? "eager" : "lazy"
      }
    ) }, idx)) }),
    /* @__PURE__ */ jsx(
      CarouselPrevious,
      {
        className: "tw:left-0 tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity",
        style: {
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: "#FFFFFF",
          border: "1px solid #DCDDDC",
          boxShadow: "none"
        },
        children: /* @__PURE__ */ jsx(CarouselArrowIcon, { direction: "left" })
      }
    ),
    /* @__PURE__ */ jsx(
      CarouselNext,
      {
        className: "tw:right-0 tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity",
        style: {
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: "#FFFFFF",
          border: "1px solid #DCDDDC",
          boxShadow: "none"
        },
        children: /* @__PURE__ */ jsx(CarouselArrowIcon, { direction: "right" })
      }
    )
  ] }) });
};
const SkeletonCard = () => /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:my-3 tw:overflow-hidden tw:animate-pulse", children: [
  /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:items-center tw:gap-3 tw:px-3 tw:py-3", children: [
    /* @__PURE__ */ jsx("div", { className: "tw:w-56 tw:h-56 tw:rounded-lg tw:bg-gray-100 tw:flex-shrink-0" }),
    /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col tw:gap-2 tw:flex-1", children: [
      /* @__PURE__ */ jsx("div", { className: "tw:h-3 tw:w-16 tw:bg-gray-100 tw:rounded" }),
      /* @__PURE__ */ jsx("div", { className: "tw:h-4 tw:w-full tw:bg-gray-100 tw:rounded" }),
      /* @__PURE__ */ jsx("div", { className: "tw:h-4 tw:w-3/4 tw:bg-gray-100 tw:rounded" }),
      /* @__PURE__ */ jsx("div", { className: "tw:h-3 tw:w-20 tw:bg-gray-100 tw:rounded" }),
      /* @__PURE__ */ jsx("div", { className: "tw:h-5 tw:w-14 tw:bg-gray-100 tw:rounded" })
    ] })
  ] }),
  /* @__PURE__ */ jsx("div", { className: "tw:px-3 tw:pb-3", children: /* @__PURE__ */ jsx("div", { className: "tw:h-11 tw:w-full tw:bg-gray-100 tw:rounded-full" }) })
] });
const ProductHighlightCard = ({
  stacklineSku,
  reason
}) => {
  const { trackProductClick } = useProductClickTracking();
  const { data: brandAppConfig } = useBrandAppConfig();
  const { apiBaseUrl } = useConciergeContext();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const primaryContrastText = theme.palette.primary.contrastText;
  const textPrimary = theme.palette.text.primary;
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await fetchProductByStacklineSku({
          stacklineSku,
          headersToInject: headers,
          apiBaseUrl
        });
        if (!cancelled && result) setProduct(result);
      } catch {
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [stacklineSku, apiBaseUrl]);
  const urlSearchParams = getAppConsistentParams(window.location.search);
  const targetUrl = product ? getProductUrl({
    title: product.title,
    stacklineSku: product.stacklineSku,
    urlSearchParams,
    dtcProductPageUrl: product.dtcProductPageUrl,
    appConfigId: brandAppConfig == null ? void 0 : brandAppConfig.appConfigId
  }) : "#";
  const handleClick = useCallback(() => {
    if (!product) return;
    trackProductClick({
      stacklineSku: product.stacklineSku,
      productTitle: product.title,
      clickSource: "highlight",
      retailerId: product.retailerId,
      retailPrice: product.retailPrice,
      hasReward: product.hasRewards,
      rewardAmount: product.totalRewardAmount
    });
  }, [trackProductClick, product]);
  if (isLoading) return /* @__PURE__ */ jsx(SkeletonCard, {});
  if (!product) return null;
  const reviewRating = product.reviewsRating > 0 ? Math.round(product.reviewsRating * 10) / 10 : product.reviewsAverageRating > 0 ? Math.round(product.reviewsAverageRating * 10) / 10 : 0;
  const reviewCount = product.reviewsCount || 0;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "tw:flex tw:items-stretch tw:gap-4 tw:px-3 tw:py-3 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:my-3 tw:overflow-hidden tw:transition-shadow tw:duration-200 hover:tw:shadow-md",
      children: [
        /* @__PURE__ */ jsx(ProductImageCarousel, { images: product.imageUrls || [], title: product.title }),
        /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col tw:justify-between tw:min-w-0 tw:flex-1 tw:py-1", children: [
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx(
              "span",
              {
                className: "tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wider",
                style: { color: textPrimary },
                children: product.brandName
              }
            ),
            /* @__PURE__ */ jsx(
              "a",
              {
                href: targetUrl,
                target: "_blank",
                rel: "noreferrer",
                onClick: handleClick,
                className: "tw:block tw:text-base tw:font-semibold tw:leading-snug tw:line-clamp-2 tw:no-underline tw:cursor-pointer tw:mt-1",
                style: { color: textPrimary },
                children: product.title
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { children: [
            reviewRating > 0 && /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:items-center tw:gap-1", children: [
              /* @__PURE__ */ jsx(StarIcon, {}),
              /* @__PURE__ */ jsxs("span", { className: "tw:text-sm tw:font-medium", style: { color: textPrimary }, children: [
                reviewRating,
                " (",
                formatReviewCount(reviewCount),
                ")"
              ] })
            ] }),
            product.hasRewards && product.totalRewardAmount > 0 && /* @__PURE__ */ jsxs(
              "span",
              {
                className: "tw:inline-flex tw:self-start tw:text-[11px] tw:font-semibold tw:px-2 tw:py-0.5 tw:rounded-full tw:mt-1",
                style: { backgroundColor: "rgba(162, 34, 114, 0.12)", color: "#A22272" },
                children: [
                  "+",
                  formatMoney(product.totalRewardAmount),
                  " reward"
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsxs(
            "a",
            {
              href: targetUrl,
              target: "_blank",
              rel: "noreferrer",
              onClick: handleClick,
              className: "tw:no-underline tw:flex tw:items-center tw:justify-center tw:gap-3 tw:w-full tw:cursor-pointer",
              style: {
                height: 38,
                backgroundColor: primaryColor,
                color: primaryContrastText,
                borderRadius: 9999,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: 0.08,
                textDecoration: "none",
                transition: "opacity 0.2s"
              },
              children: [
                /* @__PURE__ */ jsx("span", { children: "View product" }),
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    style: {
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      backgroundColor: primaryContrastText
                    }
                  }
                ),
                /* @__PURE__ */ jsx("span", { children: formatMoney(product.retailPrice) })
              ]
            }
          ),
          reason && /* @__PURE__ */ jsx(
            "span",
            {
              className: "tw:text-sm tw:leading-snug",
              style: { color: textPrimary },
              children: reason
            }
          )
        ] })
      ]
    }
  );
};
export {
  ProductHighlightCard
};
//# sourceMappingURL=ProductHighlightCard.js.map
